/**
 * 統一持股計算服務
 * 整合所有分散的持股計算邏輯，提供一致的計算結果
 */

import { Transaction, Stock } from "../types";
import { logger } from "../utils/logger";
import { StockNameManager } from "./stockNameManager";
import { DecimalHelper } from "../utils/decimalHelper";

// 分次購買批次（FIFO 扣減後剩餘的持有批次，成本加總 = investmentCost）
export interface RemainingLot {
  date: Date; // 買入（或股票股利）日期
  quantity: number; // 剩餘股數
  price: number; // 原始成交價（股票股利無買入價）
  cost: number; // 該批次剩餘成本（含手續費與調整金額）
  source: "buy" | "stock_increase"; // 批次來源
}

// 統一的持股資料介面
export interface HoldingCalculation {
  symbol: string;
  name: string;

  // 數量相關
  totalBuyQuantity: number;
  totalSellQuantity: number;
  currentQuantity: number;

  // 金額相關
  totalBuyAmount: number; // 總買入金額
  totalSellAmount: number; // 總賣出金額
  totalBuyFees: number; // 總買入手續費
  totalSellFees: number; // 總賣出手續費
  totalSellTax: number; // 總賣出稅金
  dividends: number; // 股利收入
  dividendFees: number; // 股利相關費用
  dividendTax: number; // 股利相關稅金

  // 計算欄位
  avgBuyPrice: number; // 平均買入價
  avgSellPrice: number; // 平均賣出價
  avgCost: number; // 平均成本（含手續費）

  // 投資成本
  investmentCost: number; // 目前持股投資成本
  totalCostWithFees: number; // 總成本（含手續費稅金）
  remainingLots: RemainingLot[]; // 分次購買批次明細（FIFO 扣減後剩餘）

  // 目前市值（需要股價資料）
  currentPrice: number | null; // 修正：支持 null 值以表示無股價資料
  marketValue: number;

  // 損益計算
  unrealizedGain: number; // 未實現損益
  unrealizedGainPercent: number; // 未實現損益百分比
  realizedGain: number; // 已實現損益（買賣差價）
  realizedGainPercent: number; // 已實現損益百分比
  netDividendIncome: number; // 淨股利收入（扣除費用）
  totalProfit: number; // 總收益（已實現+股利）
  totalGain: number; // 總損益（含息）= 未實現 + 已實現 + 淨股利
  totalGainPercent: number; // 總報酬率（含息）= totalGain / totalBuyAmountWithFees（總投入成本）

  // 成本分析
  costPerShareWithDividend: number; // 每股成本（含息）
  costPerShareWithoutDividend: number; // 每股成本（不含息）

  // 狀態
  isHolding: boolean; // 是否仍持有
  isCleared: boolean; // 是否已清倉
  hasTransactionHistory: boolean; // 是否有交易記錄

  // 期間時間資訊
  lastTransactionDate: Date; // 該期間最後一筆交易時間（用於排序）

  // 元資料
  lastUpdated: Date;
  dataSource: "calculated" | "cached";
}

// 歷史持股資料（已清倉）
export interface HistoricalHolding extends HoldingCalculation {
  // 已清倉股票的特殊欄位可以在這裡添加
}

// 計算選項
export interface CalculationOptions {
  includeHistorical?: boolean; // 是否包含歷史資料
  includeFees?: boolean; // 是否包含手續費計算
  useCachedStockPrices?: boolean; // 是否使用快取的股價
}

/**
 * 持股計算服務
 */
export class HoldingsCalculationService {
  /**
   * 從交易記錄計算所有持股
   */
  static calculateHoldingsFromTransactions(
    transactions: Transaction[],
    stocks: Stock[] = [],
    options: CalculationOptions = {},
  ): {
    currentHoldings: HoldingCalculation[];
    historicalHoldings: HistoricalHolding[];
    summary: {
      totalCurrentValue: number;
      totalInvestmentCost: number;
      totalUnrealizedGain: number;
      totalRealizedGain: number;
      totalDividendIncome: number;
      totalProfit: number;
    };
  } {
    const { includeHistorical = true, includeFees = true } = options;

    logger.holdings.info("開始計算持股", {
      transactionCount: transactions.length,
      stockCount: stocks.length,
      options,
    });

    // 建立股票價格查找表
    const stockPriceMap = new Map<string, Stock>();
    stocks.forEach((stock) => stockPriceMap.set(stock.symbol, stock));

    // 按股票符號分組交易
    const transactionsBySymbol = this.groupTransactionsBySymbol(transactions);

    // 計算每個股票的持股資料
    const allCalculations: HoldingCalculation[] = [];

    // 將 Map.entries() 轉為陣列來避免 TypeScript 迭代錯誤
    for (const [symbol, symbolTransactions] of Array.from(
      transactionsBySymbol.entries(),
    )) {
      // 使用新的時間序處理邏輯
      const { currentHolding, historicalHoldings } =
        this.processTransactionsChronologically(
          symbol,
          symbolTransactions,
          stockPriceMap.get(symbol),
          includeFees,
        );

      // 添加目前持股
      if (currentHolding) {
        allCalculations.push(currentHolding);
      }

      // 添加歷史持股
      allCalculations.push(...historicalHoldings);
    }

    // 分離目前持有和歷史持股
    const currentHoldings = allCalculations.filter((h) => h.isHolding);
    const historicalHoldings = includeHistorical
      ? allCalculations.filter((h) => h.isCleared && h.hasTransactionHistory)
      : [];

    // 計算總計
    const summary = this.calculateSummary(allCalculations);

    logger.holdings.info("持股計算完成", {
      currentHoldingsCount: currentHoldings.length,
      historicalHoldingsCount: historicalHoldings.length,
      summary,
    });

    return {
      currentHoldings,
      historicalHoldings: historicalHoldings as HistoricalHolding[],
      summary,
    };
  }

  /**
   * 按股票符號分組交易記錄
   */
  private static groupTransactionsBySymbol(
    transactions: Transaction[],
  ): Map<string, Transaction[]> {
    const grouped = new Map<string, Transaction[]>();

    transactions.forEach((transaction) => {
      // 只過濾現金異動（股票股利影響持股數量）
      if (
        transaction.type === "cash_deposit" ||
        transaction.type === "cash_withdrawal"
      ) {
        return;
      }

      const symbol = transaction.symbol;
      // 過濾掉無效或現金類型的symbol
      if (!symbol || symbol === "CASH" || symbol === "" || symbol === "null") {
        return;
      }

      if (!grouped.has(symbol)) {
        grouped.set(symbol, []);
      }
      grouped.get(symbol)!.push(transaction);
    });

    return grouped;
  }

  /**
   * 依時間序處理交易，當股數清空時結算一次過往紀錄
   */
  private static processTransactionsChronologically(
    symbol: string,
    transactions: Transaction[],
    stock?: Stock,
    includeFees: boolean = true,
  ): {
    currentHolding: HoldingCalculation | null;
    historicalHoldings: HoldingCalculation[];
  } {
    if (transactions.length === 0) {
      return { currentHolding: null, historicalHoldings: [] };
    }

    // 依時間排序交易（最早的在前）
    const sortedTransactions = [...transactions].sort((a, b) => {
      const dateA = a.transactionDate || a.timestamp;
      const dateB = b.transactionDate || b.timestamp;
      return new Date(dateA).getTime() - new Date(dateB).getTime();
    });

    const historicalHoldings: HoldingCalculation[] = [];
    let currentPeriodTransactions: Transaction[] = [];
    let currentQuantity = 0;

    // 逐筆處理交易
    for (const transaction of sortedTransactions) {
      currentPeriodTransactions.push(transaction);

      // 更新當前數量
      switch (transaction.type) {
        case "buy":
        case "stock_increase":
          currentQuantity += transaction.quantity;
          break;
        case "sell":
        case "stock_decrease":
          currentQuantity -= transaction.quantity;
          break;
        // dividend 不影響股數
      }

      // 當股數被清空時，結算這個期間
      if (currentQuantity <= 0) {
        const periodResult = this.calculateSinglePeriod(
          symbol,
          currentPeriodTransactions,
          stock,
          includeFees,
          true, // 標記為已清倉
        );

        if (periodResult) {
          historicalHoldings.push(periodResult);
        }

        // 重置期間交易記錄
        currentPeriodTransactions = [];
        currentQuantity = 0;
      }
    }

    // 處理剩餘的持股（未清倉）
    let currentHolding: HoldingCalculation | null = null;
    if (currentPeriodTransactions.length > 0 && currentQuantity > 0) {
      currentHolding = this.calculateSinglePeriod(
        symbol,
        currentPeriodTransactions,
        stock,
        includeFees,
        false, // 仍持有
      );
    }

    return { currentHolding, historicalHoldings };
  }

  /**
   * 計算單一期間的持股資料
   */
  private static calculateSinglePeriod(
    symbol: string,
    transactions: Transaction[],
    stock?: Stock,
    includeFees: boolean = true,
    isCleared: boolean = false,
  ): HoldingCalculation | null {
    if (transactions.length === 0) {
      return null;
    }

    // 初始化累計資料
    let totalBuyQuantity = 0;
    let totalSellQuantity = 0;
    let totalBuyAmount = 0;
    let totalSellAmount = 0;
    let totalBuyFees = 0;
    let totalSellFees = 0;
    let totalSellTax = 0;
    let dividends = 0;
    let dividendFees = 0;
    let dividendTax = 0;
    let realizedGainFromLots = 0;

    type HoldingLot = {
      quantity: number;
      cost: number;
      date: Date;
      price: number;
      source: "buy" | "stock_increase";
    };

    const lots: HoldingLot[] = [];

    const consumeLots = (
      quantity: number,
      currentTransaction?: Transaction,
    ): number => {
      let remaining = Math.round(quantity * 1000000) / 1000000;
      let matchedCost = 0;

      while (remaining > 0 && lots.length > 0) {
        const lot = lots[0];
        const executedQuantity = Math.min(lot.quantity, remaining);
        const lotCostPerShare = lot.cost / lot.quantity;
        const costPortion = lotCostPerShare * executedQuantity;

        matchedCost += costPortion;
        lot.quantity -= executedQuantity;
        lot.cost -= costPortion;
        remaining -= executedQuantity;

        if (lot.quantity <= 1e-8) {
          lots.shift();
        }
      }

      if (remaining > 1e-6) {
        const buyCount = transactions.filter((t) => t.type === "buy").length;
        const sellCount = transactions.filter((t) => t.type === "sell").length;
        const dividendCount = transactions.filter(
          (t) => t.type === "dividend",
        ).length;
        const accountId = currentTransaction?.accountId;

        if (buyCount > 0) {
          // (CLEAN-02:移除遺留 console.log 與其組字串,完整交易明細不應輸出到 console)

          logger.holdings.warn("賣出數量超過持有股數，無法完全匹配成本", {
            symbol,
            accountId,
            remaining,
            sellQuantity: currentTransaction?.quantity,
            sellDate: (() => {
              const d =
                currentTransaction?.transactionDate ||
                currentTransaction?.timestamp;
              return d ? new Date(d).toISOString().slice(0, 10) : "unknown";
            })(),
            lotsCount: lots.length,
            totalLotQuantity: lots.reduce((sum, lot) => sum + lot.quantity, 0),
            totalTransactions: transactions.length,
            buyCount,
            sellCount,
            dividendCount,
          });
        } else {
          logger.holdings.debug("賣出記錄無對應買入（歷史數據）", {
            symbol,
            accountId,
            sellQuantity: currentTransaction?.quantity,
            sellCount,
            dividendCount,
          });
        }
      }

      return matchedCost;
    };

    // 處理所有交易
    transactions.forEach((transaction) => {
      const adjustmentAmount = transaction.adjustmentAmount || 0;

      switch (transaction.type) {
        case "buy":
          totalBuyQuantity += transaction.quantity;
          // 使用標準化的成交金額計算（無條件捨去到整數元,與券商一致）
          const buySubtotal = DecimalHelper.calculateTradeAmount(
            transaction.quantity,
            transaction.price,
          );
          totalBuyAmount += buySubtotal;
          let buyLotCost = buySubtotal;
          if (includeFees) {
            totalBuyFees += transaction.fees || 0;
            // 買入時的調整金額會增加成本
            totalBuyFees += adjustmentAmount;
            buyLotCost += (transaction.fees || 0) + adjustmentAmount;
          }
          lots.push({
            quantity: transaction.quantity,
            cost: buyLotCost,
            date: new Date(transaction.transactionDate || transaction.timestamp),
            price: transaction.price,
            source: "buy",
          });
          break;

        case "sell":
          totalSellQuantity += transaction.quantity;
          // 使用標準化的成交金額計算（無條件捨去到整數元,與券商一致）
          const sellSubtotal = DecimalHelper.calculateTradeAmount(
            transaction.quantity,
            transaction.price,
          );
          totalSellAmount += sellSubtotal;
          let sellAdjustmentTotal = 0;
          let sellFees = 0;
          let sellTaxes = 0;
          if (includeFees) {
            sellFees = transaction.fees || 0;
            sellTaxes = transaction.taxes || 0;
            totalSellFees += sellFees;
            totalSellTax += sellTaxes;
            // 賣出時的調整金額會影響收入（正數增加收入，負數減少收入）
            totalSellAmount += adjustmentAmount;
            sellAdjustmentTotal = adjustmentAmount;
          }
          const sellNetIncome =
            sellSubtotal + sellAdjustmentTotal - sellFees - sellTaxes;
          const sellCost = consumeLots(transaction.quantity, transaction);
          realizedGainFromLots += sellNetIncome - sellCost;
          break;

        case "dividend":
          // 股利金額:無條件捨去到整數元,與交易儲存的 total (calculateTradeAmount) 一致
          const dividendSubtotal = DecimalHelper.calculateCashDividend(
            transaction.quantity,
            transaction.price,
          );
          dividends += dividendSubtotal;
          if (includeFees) {
            dividendFees += transaction.fees || 0;
            dividendTax += transaction.taxes || 0;
            // 股利時的調整金額會影響股利收入
            dividends += adjustmentAmount;
          }
          break;

        case "stock_increase":
          totalBuyQuantity += transaction.quantity;
          // 股票股利無成本
          lots.push({
            quantity: transaction.quantity,
            cost: 0,
            date: new Date(transaction.transactionDate || transaction.timestamp),
            price: transaction.price,
            source: "stock_increase",
          });
          break;

        case "stock_decrease":
          totalSellQuantity += transaction.quantity;
          // 減資等無收入
          const decreaseCost = consumeLots(transaction.quantity, transaction);
          realizedGainFromLots -= decreaseCost;
          break;
      }
    });

    const remainingLotCost = lots.reduce((sum, lot) => sum + lot.cost, 0);
    const normalizedRemainingLotCost =
      Math.abs(remainingLotCost) < 1e-4 ? 0 : remainingLotCost;

    // 剩餘批次明細（供分次購買成本 Popover 顯示，與 investmentCost 對帳）
    const remainingLots: RemainingLot[] = lots
      .filter((lot) => lot.quantity > 1e-8)
      .map((lot) => ({
        date: lot.date,
        quantity: lot.quantity,
        price: lot.price,
        cost: lot.cost,
        source: lot.source,
      }));

    return this.createHoldingCalculation({
      symbol,
      stock,
      totalBuyQuantity,
      totalSellQuantity,
      totalBuyAmount,
      totalSellAmount,
      totalBuyFees,
      totalSellFees,
      totalSellTax,
      dividends,
      dividendFees,
      dividendTax,
      transactions,
      isCleared,
      realizedGainOverride: realizedGainFromLots,
      investmentCostOverride: normalizedRemainingLotCost,
      remainingLots,
    });
  }

  /**
   * 計算單一股票的持股資料（向後相容方法，內部使用統一計算邏輯）
   */
  private static calculateSingleHolding(
    symbol: string,
    transactions: Transaction[],
    stock?: Stock,
    includeFees: boolean = true,
  ): HoldingCalculation | null {
    if (transactions.length === 0) {
      return null;
    }
    let totalBuyQuantity = 0;
    let totalSellQuantity = 0;

    transactions.forEach((transaction) => {
      switch (transaction.type) {
        case "buy":
        case "stock_increase":
          totalBuyQuantity += transaction.quantity;
          break;
        case "sell":
        case "stock_decrease":
          totalSellQuantity += transaction.quantity;
          break;
      }
    });

    const currentQuantity = totalBuyQuantity - totalSellQuantity;
    const isCleared =
      currentQuantity === 0 && totalBuyQuantity > 0 && totalSellQuantity > 0;

    return this.calculateSinglePeriod(
      symbol,
      transactions,
      stock,
      includeFees,
      isCleared,
    );
  }

  /**
   * 建立 HoldingCalculation 物件
   */
  private static createHoldingCalculation(params: {
    symbol: string;
    stock?: Stock;
    totalBuyQuantity: number;
    totalSellQuantity: number;
    totalBuyAmount: number;
    totalSellAmount: number;
    totalBuyFees: number;
    totalSellFees: number;
    totalSellTax: number;
    dividends: number;
    dividendFees: number;
    dividendTax: number;
    transactions: Transaction[];
    isCleared: boolean;
    realizedGainOverride?: number;
    investmentCostOverride?: number;
    remainingLots?: RemainingLot[];
  }): HoldingCalculation {
    const {
      symbol,
      stock,
      totalBuyQuantity,
      totalSellQuantity,
      totalBuyAmount,
      totalSellAmount,
      totalBuyFees,
      totalSellFees,
      totalSellTax,
      dividends,
      dividendFees,
      dividendTax,
      transactions,
      isCleared,
      realizedGainOverride,
      investmentCostOverride,
      remainingLots,
    } = params;

    // 計算基本指標（使用適當的精度控制）
    const currentQuantity = totalBuyQuantity - totalSellQuantity;
    const avgBuyPrice =
      totalBuyQuantity > 0
        ? Math.round((totalBuyAmount / totalBuyQuantity) * 100) / 100
        : 0;
    const avgSellPrice =
      totalSellQuantity > 0
        ? Math.round((totalSellAmount / totalSellQuantity) * 100) / 100
        : 0;

    // 計算平均成本（含手續費，保留2位小數）
    const totalBuyAmountWithFees = totalBuyAmount + totalBuyFees;
    const avgCostPrecise =
      totalBuyQuantity > 0 ? totalBuyAmountWithFees / totalBuyQuantity : 0;
    const avgCost =
      totalBuyQuantity > 0 ? Math.round(avgCostPrecise * 100) / 100 : 0;

    // 目前持股的投資成本（四捨五入到整數元）
    const computedInvestmentCost =
      investmentCostOverride ?? currentQuantity * avgCost;
    const investmentCost = Math.round(computedInvestmentCost);

    // 總成本（含所有費用）
    const totalCostWithFees =
      totalBuyAmountWithFees + totalSellFees + totalSellTax;

    // 股價相關計算 - 修正：沒有股價時不使用 0，避免計算錯誤
    const currentPrice =
      stock?.currentPrice && stock.currentPrice > 0 ? stock.currentPrice : null;
    const marketValue =
      currentPrice !== null && currentQuantity > 0
        ? currentQuantity * currentPrice
        : 0;

    // 未實現損益 - 只在有有效股價時計算（四捨五入到整數元，百分比保留2位小數）
    const unrealizedGain =
      currentPrice !== null && currentQuantity > 0
        ? Math.round(marketValue - investmentCost)
        : 0;
    const unrealizedGainPercent =
      currentPrice !== null && investmentCost > 0
        ? Math.round((unrealizedGain / investmentCost) * 10000) / 100
        : 0;

    // 已實現損益（賣出淨收入 - 賣出成本，四捨五入到整數元）
    const sellNetIncome = totalSellAmount - totalSellFees - totalSellTax;
    const sellCost = Math.round(totalSellQuantity * avgCost);
    const realizedGainBase = realizedGainOverride ?? sellNetIncome - sellCost;
    const realizedGain = Math.round(realizedGainBase);
    const realizedGainPercent =
      sellCost > 0 ? Math.round((realizedGain / sellCost) * 10000) / 100 : 0;

    // 淨股利收入（四捨五入到整數元）
    const netDividendIncome = Math.round(
      dividends - dividendFees - dividendTax,
    );

    // 總收益（四捨五入到整數元）
    const totalProfit = Math.round(realizedGain + netDividendIncome);

    // 總損益（含息）= 未實現損益 + 已實現損益 + 淨股利收入（四捨五入到整數元）
    const totalGain = Math.round(
      unrealizedGain + realizedGain + netDividendIncome,
    );
    // 總報酬率分母採用總投入成本（含手續費），因 totalGain 含已賣出部位之已實現損益，
    // 若只用剩餘持股投資成本會在部分停利後被過度放大
    const totalGainPercent =
      totalBuyAmountWithFees > 0
        ? Math.round((totalGain / totalBuyAmountWithFees) * 10000) / 100
        : 0;

    // 成本分析（保留2位小數精度）
    // 計算持有期間所承擔的總成本（買入成本 + 賣出時的費用稅金分攤）
    // 概念：每股承擔的成本 = (買入總成本 + 賣出費用稅金) / 買入總股數
    const totalTransactionCosts =
      totalBuyAmountWithFees + totalSellFees + totalSellTax;
    const baseCostPerShare =
      totalBuyQuantity > 0
        ? Math.round((totalTransactionCosts / totalBuyQuantity) * 100) / 100
        : 0;

    // 成本/股(不含息)：不考慮股利對成本的攤薄
    const costPerShareWithoutDividend = baseCostPerShare;

    // 成本/股(含息)：考慮股利對成本的攤薄效果
    // 股利攤薄係數：淨股利收入除以買入總股數，等於每股獲得的股利
    const dividendPerShare =
      totalBuyQuantity > 0
        ? Math.round((netDividendIncome / totalBuyQuantity) * 100) / 100
        : 0;
    const costPerShareWithDividend =
      Math.round((baseCostPerShare - dividendPerShare) * 100) / 100;

    // 狀態判斷
    const isHolding = !isCleared && currentQuantity > 0;
    const hasTransactionHistory = transactions.length > 0;

    // 取得該期間最後一筆交易時間（用於過往紀錄排序）
    const lastTransactionDate = new Date(
      Math.max(
        ...transactions.map((t) =>
          new Date(t.transactionDate || t.timestamp).getTime(),
        ),
      ),
    );

    return {
      symbol,
      name: StockNameManager.getStockName(symbol),

      // 數量
      totalBuyQuantity,
      totalSellQuantity,
      currentQuantity,

      // 金額
      totalBuyAmount,
      totalSellAmount,
      totalBuyFees,
      totalSellFees,
      totalSellTax,
      dividends,
      dividendFees,
      dividendTax,

      // 計算欄位
      avgBuyPrice,
      avgSellPrice,
      avgCost,

      // 投資成本
      investmentCost,
      totalCostWithFees,
      remainingLots: remainingLots ?? [],

      // 目前市值
      currentPrice,
      marketValue,

      // 損益
      unrealizedGain,
      unrealizedGainPercent,
      realizedGain,
      realizedGainPercent,
      netDividendIncome,
      totalProfit,
      totalGain,
      totalGainPercent,

      // 成本分析
      costPerShareWithDividend,
      costPerShareWithoutDividend,

      // 狀態
      isHolding,
      isCleared,
      hasTransactionHistory,

      // 期間時間資訊
      lastTransactionDate,

      // 元資料
      lastUpdated: new Date(),
      dataSource: "calculated" as const,
    };
  }

  /**
   * 計算總計摘要
   */
  private static calculateSummary(holdings: HoldingCalculation[]) {
    const currentHoldings = holdings.filter((h) => h.isHolding);
    const allHoldings = holdings;

    return {
      totalCurrentValue: currentHoldings.reduce(
        (sum, h) => sum + h.marketValue,
        0,
      ),
      totalInvestmentCost: currentHoldings.reduce(
        (sum, h) => sum + h.investmentCost,
        0,
      ),
      totalUnrealizedGain: currentHoldings.reduce(
        (sum, h) => sum + h.unrealizedGain,
        0,
      ),
      totalRealizedGain: allHoldings.reduce(
        (sum, h) => sum + h.realizedGain,
        0,
      ),
      totalDividendIncome: allHoldings.reduce(
        (sum, h) => sum + h.netDividendIncome,
        0,
      ),
      totalProfit: allHoldings.reduce((sum, h) => sum + h.totalProfit, 0),
    };
  }

  /**
   * 取得特定股票的持股計算
   */
  static calculateSingleStockHolding(
    symbol: string,
    transactions: Transaction[],
    stock?: Stock,
    options: CalculationOptions = {},
  ): HoldingCalculation | null {
    const symbolTransactions = transactions.filter((t) => t.symbol === symbol);
    return this.calculateSingleHolding(
      symbol,
      symbolTransactions,
      stock,
      options.includeFees !== false,
    );
  }

  /**
   * 驗證計算結果的一致性
   */
  static validateCalculation(holding: HoldingCalculation): {
    isValid: boolean;
    errors: string[];
    warnings: string[];
  } {
    const errors: string[] = [];
    const warnings: string[] = [];

    // 基本數值檢查
    if (holding.currentQuantity < 0) {
      errors.push("持股數量不能為負數");
    }

    if (
      holding.totalBuyQuantity < holding.totalSellQuantity &&
      holding.currentQuantity !== 0
    ) {
      errors.push("賣出數量超過買入數量");
    }

    if (holding.marketValue < 0) {
      warnings.push("市值為負數，可能股價資料有誤");
    }

    if (holding.avgCost <= 0 && holding.currentQuantity > 0) {
      warnings.push("平均成本為零或負數");
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }
}

// 匯出便利函數
export const calculateHoldings =
  HoldingsCalculationService.calculateHoldingsFromTransactions;
export const calculateSingleHolding =
  HoldingsCalculationService.calculateSingleStockHolding;
export const validateHolding = HoldingsCalculationService.validateCalculation;
